/**
 * Per-instance macro-step capture targeting (production-readiness Phase 5,
 * R13 host) — extracted from `StatefulMacroCoordinator` (Phase 8, review fix
 * SF5) to keep the coordinator within the file-size budget. Behavior is
 * identical to the pre-extraction private method.
 *
 * When an android stream is active, every live instance gets its own
 * capture on its own monitor: one `requestMacroStep` per instance, targeted
 * by `{ streamId, gameInstanceIndex }` and issued concurrently (the
 * service's keyed waiter map correlates each reply by its echoed identity).
 *
 * Locked decisions (do NOT re-litigate here):
 *  - Reference trees/snapshots stay PER-CAMPAIGN (Phase 1 decision): all
 *    instances run the same game, so one tree serves every instance. Only
 *    runtime capture/routing carries the instance dimension.
 *  - Walks stay PER-LAUNCH (Aidan's Phase 2 follow-up): the single walk
 *    advances on the FIRST live instance's hashes. Instances are expected
 *    in lockstep; a divergence is logged as a warning (per-instance walk
 *    state needs a MACRO_WALK_* schema dimension — out of scope here).
 *
 * Non-android launches (no active stream id) keep the legacy aggregate
 * capture path untouched. An active stream with zero live instances fails
 * the step honestly — an aggregate capture would silently read the wrong
 * monitor.
 */

import type {
  IPlayjectorService,
  MacroStepCaptureSpec,
  MacroStepCaptureResult,
} from '../../services/playjector/interfaces/IPlayjectorService';

export interface MacroStepCaptureTargetingDeps {
  playjectorService: IPlayjectorService;
  /** Active android stream id; absent/undefined → legacy aggregate capture path. */
  getActiveAndroidStreamId?: () => string | undefined;
  /** Live instance indices of the active stream. */
  getLiveGameInstanceIndices?: () => number[];
  /**
   * Wraps a single `requestMacroStep` call with the coordinator's
   * outstanding-slot retry + stale-launch detection (see
   * `StatefulMacroCoordinator.requestMacroStepForCurrentLaunch`). Falls back
   * to a bare `playjectorService.requestMacroStep` call when omitted (e.g.
   * unit tests exercising this module in isolation).
   */
  requestStep?: (
    capturesByCaptureId: Record<string, MacroStepCaptureSpec>,
    target?: { streamId: string; gameInstanceIndex: number },
  ) => Promise<Record<string, MacroStepCaptureResult> | 'stale'>;
}

/**
 * Resolve a walk step's captures: one aggregate request when no android
 * stream is active, otherwise one targeted request per live instance with
 * the walk advancing on the first instance's hashes (see header).
 */
export async function requestStepCaptures(
  deps: MacroStepCaptureTargetingDeps,
  capturesByCaptureId: Record<string, MacroStepCaptureSpec>,
): Promise<Record<string, MacroStepCaptureResult> | 'stale'> {
  const requestStep =
    deps.requestStep ??
    ((captures: Record<string, MacroStepCaptureSpec>, target?: { streamId: string; gameInstanceIndex: number }) =>
      deps.playjectorService.requestMacroStep(captures, undefined, target));

  const streamId = deps.getActiveAndroidStreamId?.();
  if (!streamId) {
    return requestStep(capturesByCaptureId);
  }

  const indices = deps.getLiveGameInstanceIndices?.() ?? [];
  if (indices.length === 0) {
    throw new Error(`Android stream "${streamId}" has no live instances to capture from`);
  }

  const perInstance = await Promise.all(
    indices.map((gameInstanceIndex) => requestStep(capturesByCaptureId, { streamId, gameInstanceIndex })),
  );

  if (perInstance.some((result) => result === 'stale')) {
    return 'stale';
  }
  const resolved = perInstance as Record<string, MacroStepCaptureResult>[];

  const [first, ...rest] = resolved;
  rest.forEach((other, i) => {
    const diverged = Object.keys(first).some(
      (captureId) => first[captureId]?.hash !== other[captureId]?.hash,
    );
    if (diverged) {
      console.warn(
        '[StatefulMacroSystemStep] Instance capture diverged from the first instance — ' +
        'the per-launch walk advances on the first instance (per-instance walks are a Phase 2 follow-up)',
        { streamId, gameInstanceIndex: indices[i + 1] },
      );
    }
  });
  return first;
}
